import React, { useCallback } from 'react';

import { Tab, Tabs } from '@mui/material';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import useOptimisticValueReact17 from 'hooks/useOptimisticValueReact17';

import { getTabStyles, indicatorStyles, wrapperStyles } from './SectionTabs.styles';
import { getActiveTabValue } from './SectionTabs.utils';

const tabIndicatorProps = {
  sx: indicatorStyles,
  className: 'tabs-indicator',
};

const SectionTabs = ({ value, onChange, tabs }) => {
  const [optimisticValue, setOptimisticValue] = useOptimisticValueReact17(value);
  const activeValue = getActiveTabValue({ tabs, optimisticValue, value });

  const handleTabClick = useCallback(
    (tabValue, originalOnClick) => () => {
      setOptimisticValue(tabValue, originalOnClick);
    },
    [setOptimisticValue],
  );

  return (
    <Tabs
      visibleScrollbar
      TabIndicatorProps={tabIndicatorProps}
      className="tabs"
      orientation="vertical"
      scrollButtons={false}
      sx={wrapperStyles}
      textColor="secondary"
      value={activeValue}
      variant="scrollable"
      onChange={onChange}
    >
      {tabs.map(
        ({ label, value: tabValue, onClick, customStyles, additionalProps, isDeleted }, index) => (
          <Tab
            className={clsx('tab', { selected: tabValue === activeValue })}
            disabled={isDeleted}
            key={tabValue ?? index}
            label={label}
            role="button"
            sx={getTabStyles(customStyles, isDeleted)}
            value={tabValue ?? index}
            onClick={handleTabClick(tabValue ?? index, onClick)}
            {...additionalProps}
          />
        ),
      )}
    </Tabs>
  );
};

const valuePropType = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

SectionTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      additionalProps: PropTypes.object,
      value: valuePropType,
      isDeleted: PropTypes.bool,
      onClick: PropTypes.func,
    }),
  ).isRequired,
  value: valuePropType.isRequired,

  onChange: PropTypes.func,
};

export default SectionTabs;
